import { AccordionDetailsProps, AccordionProps, AccordionSummaryProps } from '@mui/material';
import MUIAccordion from '@mui/material/Accordion';
import MUIAccordionDetails from '@mui/material/AccordionDetails';
import MUIAccordionSummary from '@mui/material/AccordionSummary';
import React, { useState } from 'react';
import { useTheme } from 'styled-components';
import { Icon } from '../icon/index';
import { Typography } from '../typography/index';

interface StyledAccordionProps extends Omit<AccordionProps, 'title'> {
	title: string | React.ReactNode;
	description?: string;
	deferLoad?: boolean;
	disableBottomBorder?: boolean;
	padLeft?: boolean;
}

interface StyledAccordionSummaryProps extends Omit<AccordionSummaryProps, 'title'> {
	title: string | React.ReactNode;
}

interface StyledAccordionDetailsProps extends AccordionDetailsProps {
	description?: string;
}

type MuiAccordionProps = AccordionProps & { padLeft?: boolean };

export const MuiAccordion = ({ sx, padLeft, ...props }: MuiAccordionProps) => {
	return (
		<MUIAccordion
			sx={{
				border: 'none',
				'&:before': {
					display: 'none'
				},
				'& .MuiAccordionSummary-root': {
					backgroundColor: 'background.paper-colour',
					...(!padLeft && { paddingLeft: 0 })
				},
				...(!padLeft && {
					'& .MuiAccordionDetails-root': {
						paddingLeft: 0
					}
				}),
				...sx
			}}
			{...props}
		>
			{props.children}
		</MUIAccordion>
	);
};

export const AccordionSummary = (props: AccordionSummaryProps) => {
	return <MUIAccordionSummary {...props}>{props.children}</MUIAccordionSummary>;
};
export const AccordionDetails = (props: AccordionDetailsProps) => {
	return <MUIAccordionDetails {...props}>{props.children}</MUIAccordionDetails>;
};

export const Accordion: React.FC<StyledAccordionProps> = ({
	title,
	description,
	children,
	defaultExpanded,
	deferLoad = false,
	disableBottomBorder = false,
	disableGutters = true,
	elevation = 0,
	square = true,
	padLeft = false,
	sx,
	...props
}) => {
	const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded ?? false);
	const theme = useTheme();

	const accordionStyles = {
		borderBottom: disableBottomBorder ? undefined : `1px solid ${theme.palette.grey[300]}`,
		paddingLeft: padLeft ? '1rem' : undefined,
		'&.Mui-expanded:last-of-type': {
			borderBottom: 0
		},
		'& .MuiAccordionSummary-root': {
			padding: 0
		},
		...sx
	};

	return (
		<MuiAccordion
			{...props}
			expanded={isExpanded}
			onChange={(_, expanded) => setIsExpanded(expanded)}
			disableGutters={disableGutters}
			elevation={elevation}
			square={square}
			sx={accordionStyles}
		>
			<StyledAccordionSummary title={title} />
			<AccordionDetails>{deferLoad ? (isExpanded ? children : null) : children}</AccordionDetails>
		</MuiAccordion>
	);
};

export const StyledAccordionSummary = ({ title, ...props }: StyledAccordionSummaryProps) => {
	return (
		<AccordionSummary
			expandIcon={<Icon iconName="KeyboardArrowDown" />}
			sx={{
				'& .MuiAccordionSummary-content': {
					marginTop: '1.2rem',
					marginBottom: '1.2rem'
				}
			}}
			{...props}
		>
			{typeof title === 'string' ? <Typography variant="h5">{title}</Typography> : title}
		</AccordionSummary>
	);
};
export const StyledAccordionDetails = ({ description, children, ...props }: StyledAccordionDetailsProps) => {
	return (
		<MUIAccordionDetails
			{...props}
			sx={{
				padding: '0 0 1.5rem 0'
			}}
		>
			{description ? (
				<Typography variant="body1" color={theme => theme.palette.text.secondary}>
					{description}
				</Typography>
			) : null}
			{children}
		</MUIAccordionDetails>
	);
};
